import * as SelectPrimitive from '@radix-ui/react-select'
import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'

const EMPTY_VALUE = '__empty__'

export type SelectOption = {
  value: string
  label: string
}

export type SelectFieldProps = {
  id: string
  label: string
  value?: string
  onValueChange: (value: string | undefined) => void
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  allowEmpty?: boolean
  emptyLabel?: string
  error?: string
  layout?: 'stacked' | 'inline'
  className?: string
}

const triggerClassName =
  'flex w-full items-center justify-between gap-2 rounded-button-sm border border-foreground/10 bg-surface-muted px-4 py-4 text-sm text-foreground outline-none transition-colors focus:border-primary-focus focus:ring-2 focus:ring-primary-focus/30 disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted-foreground'

export const SelectField = ({
  id,
  label,
  value,
  onValueChange,
  options,
  placeholder = 'Select an option',
  disabled = false,
  allowEmpty = false,
  emptyLabel = 'All',
  error,
  layout = 'stacked',
  className,
}: SelectFieldProps) => {
  const isInline = layout === 'inline'

  return (
    <div
      className={cn(
        isInline ? 'flex flex-row items-center gap-4' : 'flex flex-col gap-2',
        className
      )}
    >
      <label
        htmlFor={id}
        className={cn(
          'text-sm font-semibold text-foreground',
          isInline && 'w-44 shrink-0'
        )}
      >
        {label}
      </label>

      <div className={cn('flex min-w-0 flex-col gap-2', isInline && 'flex-1')}>
        <SelectPrimitive.Root
          value={value ?? (allowEmpty ? EMPTY_VALUE : undefined)}
          onValueChange={(nextValue) => {
            onValueChange(nextValue === EMPTY_VALUE ? undefined : nextValue)
          }}
          disabled={disabled}
        >
          <SelectPrimitive.Trigger
            id={id}
            className={triggerClassName}
            aria-invalid={Boolean(error)}
          >
            <SelectPrimitive.Value placeholder={placeholder} />
            <SelectPrimitive.Icon asChild>
              <ChevronDown className="size-4 shrink-0 opacity-60" aria-hidden />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>

          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              className="z-60 overflow-hidden rounded-button-sm border border-foreground/10 bg-background shadow-md"
              position="popper"
              sideOffset={4}
            >
              <SelectPrimitive.Viewport className="p-1">
                {allowEmpty ? (
                  <SelectPrimitive.Item
                    value={EMPTY_VALUE}
                    className="relative flex cursor-pointer select-none items-center rounded-button-sm px-3 py-2 text-sm outline-none data-highlighted:bg-surface-muted data-[state=checked]:font-semibold"
                  >
                    <SelectPrimitive.ItemText>{emptyLabel}</SelectPrimitive.ItemText>
                  </SelectPrimitive.Item>
                ) : null}

                {options.map((option) => (
                  <SelectPrimitive.Item
                    key={option.value}
                    value={option.value}
                    className="relative flex cursor-pointer select-none items-center rounded-button-sm px-3 py-2 text-sm outline-none data-highlighted:bg-surface-muted data-[state=checked]:font-semibold"
                  >
                    <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                  </SelectPrimitive.Item>
                ))}
              </SelectPrimitive.Viewport>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  )
}
